import React, { useState } from "react";
import { MdClose, MdWarning } from "react-icons/md";
import { useTranslation } from "react-i18next";

export default function WithdrawModal({ setModalOpen, userData, onWithdraw }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: 경고, 2: 비밀번호 입력, 3: 최종 확인
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const CONFIRM_TEXT = t("common.myInfo.withdrawConfirmText") || "회원탈퇴";

  const handleNextStep = () => {
    setError("");
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (!password) {
        setError(
          t("common.myInfo.withdrawPasswordError") ||
            "비밀번호를 입력해 주세요."
        );
        return;
      }
      setStep(3);
    }
  };

  const handleWithdraw = async () => {
    if (confirmText !== CONFIRM_TEXT) {
      setError(
        `"${CONFIRM_TEXT}"${
          t("common.myInfo.withdrawInputError") || "를 정확히 입력해 주세요."
        }`
      );
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      if (onWithdraw) {
        await onWithdraw({ password });
        // 성공 시 MyInfo에서 로그아웃 및 페이지 이동 처리함
      } else {
        // API 연결 전 임시 처리
        console.log("회원탈퇴 요청:", {
          username: userData?.username,
          password,
        });
        alert(
          t("common.myInfo.withdrawNotConnected") ||
            "회원탈퇴 기능은 아직 연결되지 않았습니다."
        );
        setIsLoading(false);
      }
    } catch (err) {
      // 서버 에러 메시지 처리
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        t("common.myInfo.withdrawProcessError") ||
        "탈퇴 처리 중 오류가 발생했습니다.";
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setModalOpen(false);
    }
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 }}
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-3 shadow-lg"
        style={{ maxWidth: "450px", width: "90%" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          className="d-flex justify-content-between align-items-center p-3"
          style={{ borderBottom: "1px solid #dee2e6" }}
        >
          <h5 className="m-0 text-danger d-flex align-items-center">
            <MdWarning className="me-2" size={24} />
            {t("common.myInfo.withdraw") || "회원탈퇴"}
          </h5>
          <MdClose
            size={24}
            onClick={handleClose}
            style={{ cursor: isLoading ? "not-allowed" : "pointer" }}
          />
        </div>

        {/* 본문 */}
        <div className="p-4">
          {/* Step 1: 경고 */}
          {step === 1 && (
            <div>
              <div
                className="alert alert-danger"
                style={{ backgroundColor: "#fff3cd", borderColor: "#ffc107" }}
              >
                <h6 className="fw-bold text-danger">
                  ⚠️ {t("common.myInfo.withdrawWarning") || "주의사항"}
                </h6>
                <ul className="mb-0 ps-3" style={{ fontSize: "14px" }}>
                  <li>
                    {t("common.myInfo.withdrawWarning1") ||
                      "탈퇴 시 모든 계정 정보가 삭제됩니다."}
                  </li>
                  <li>
                    {t("common.myInfo.withdrawWarning2") ||
                      "자녀와의 연결 정보가 해제됩니다."}
                  </li>
                  <li>
                    {t("common.myInfo.withdrawWarning3") ||
                      "삭제된 정보는 복구할 수 없습니다."}
                  </li>
                  <li>
                    {t("common.myInfo.withdrawWarning4") ||
                      "동일한 이메일로 재가입이 가능합니다."}
                  </li>
                </ul>
              </div>
              <p className="text-muted" style={{ fontSize: "14px" }}>
                <strong>{userData?.fullname}</strong>
                {t("common.myInfo.withdrawConfirmQuestion") ||
                  "님, 정말 탈퇴하시겠습니까?"}
              </p>
            </div>
          )}

          {/* Step 2: 비밀번호 입력 */}
          {step === 2 && (
            <div>
              <p className="mb-3" style={{ fontSize: "14px" }}>
                {t("common.myInfo.withdrawPasswordPrompt") ||
                  "본인 확인을 위해 비밀번호를 입력해 주세요."}
              </p>
              <div className="mb-3">
                <label
                  className="form-label fw-bold"
                  style={{ fontSize: "14px" }}
                >
                  {t("common.user.password") || "비밀번호"}
                </label>
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={
                    t("common.myInfo.withdrawPasswordPlaceholder") ||
                    "현재 비밀번호 입력"
                  }
                  autoComplete="current-password"
                />
              </div>
            </div>
          )}

          {/* Step 3: 최종 확인 */}
          {step === 3 && (
            <div>
              <div className="alert alert-danger">
                <strong>
                  {t("common.myInfo.withdrawFinalConfirm") || "마지막 확인"}
                </strong>
                <p className="mb-0 mt-2" style={{ fontSize: "14px" }}>
                  {t("common.myInfo.withdrawFinalPrompt") ||
                    "탈퇴를 진행하려면 아래에"}{" "}
                  <strong>"{CONFIRM_TEXT}"</strong>
                  {t("common.myInfo.withdrawFinalPrompt2") ||
                    "를 정확히 입력해 주세요."}
                </p>
              </div>
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={`"${CONFIRM_TEXT}" ${
                    t("common.myInfo.withdrawConfirmPlaceholder") || "입력"
                  }`}
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          {/* 에러 메시지 */}
          {error && (
            <div
              className="alert alert-danger py-2"
              style={{ fontSize: "14px" }}
            >
              {error}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div
          className="d-flex justify-content-end gap-2 p-3"
          style={{ borderTop: "1px solid #dee2e6" }}
        >
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("common.labels.cancel") || "취소"}
          </button>

          {step < 3 ? (
            <button
              className="btn btn-warning"
              onClick={handleNextStep}
              disabled={isLoading}
            >
              {step === 1
                ? t("common.myInfo.withdrawContinue") || "계속 진행"
                : t("common.myInfo.withdrawNext") || "다음"}
            </button>
          ) : (
            <button
              className="btn btn-danger"
              onClick={handleWithdraw}
              disabled={isLoading || confirmText !== CONFIRM_TEXT}
            >
              {isLoading
                ? t("common.myInfo.withdrawProcessing") || "처리중..."
                : t("common.myInfo.withdrawExecute") || "탈퇴하기"}
            </button>
          )}
        </div>

        {/* 단계 표시 */}
        <div className="text-center pb-3">
          <small className="text-muted">
            {step}/3 {t("common.myInfo.withdrawStep") || "단계"}
          </small>
        </div>
      </div>
    </div>
  );
}
